import { gql, useLazyQuery, useQuery, useSuspenseQuery } from '@apollo/client';
import { AssociationResourceType, RoleV2 } from '../../common';

export const GET_CURRENT_USER = gql`
    query GetCurrentUser {
        currentUser {
            id
            emailAddress
            firstName
            lastName
            status
            userRole
            auditMetadata {
                createdAt
            }
            entityAssociations {
                nodes {
                    userRoles {
                        name
                    }
                    parentResource {
                        ... on Organization {
                            id
                            name
                            organizationType
                        }
                        ... on Team {
                            id
                        }
                    }
                }
            }
            userRequests {
                nodes {
                    id
                    userRequestStatus
                    userRequestResourceType
                    resourceIds
                    userRequestExpiredAt
                    organization {
                        id
                    }
                }
            }
        }
    }
`;

export const useGetCurrentUserLazy = () => {
    return useLazyQuery(GET_CURRENT_USER, {});
};

export const useGetCurrentUser = () => {
    return useQuery(GET_CURRENT_USER, { errorPolicy: 'all' });
};

export const useSuspenseCurrentUser = () => {
    return useSuspenseQuery(GET_CURRENT_USER, {});
};

type ORG_ROLES = {
    userRoles: { name: string }[];
    parentResource: { __typename: AssociationResourceType.ORGANIZATION; name: string; id: string; organizationType: string };
};

type TEAM_ROLES = {
    userRoles: { name: string }[];
    parentResource: { __typename: AssociationResourceType.TEAM; id: string };
};

export const extractOrgFromEntityAssociations = (nodes: (ORG_ROLES | TEAM_ROLES)[] | undefined) => {
    if (!nodes) {
        return {
            name: undefined,
            organizationId: undefined,
            organizationType: undefined,
        };
    }
    const learnerAssociation: ORG_ROLES[] = nodes.filter(
        (entityAssociation): entityAssociation is ORG_ROLES =>
            entityAssociation.parentResource?.__typename === AssociationResourceType.ORGANIZATION &&
            entityAssociation.userRoles?.some(({ name: roleName }) => roleName === RoleV2.LEARNER),
    );
    if (learnerAssociation.length === 1) {
        return {
            name: learnerAssociation[0].parentResource?.name,
            organizationId: learnerAssociation[0].parentResource?.id,
            organizationType: learnerAssociation[0].parentResource?.organizationType,
        };
    } else {
        return {
            name: undefined,
            organizationId: undefined,
            organizationType: undefined,
        };
    }
};

export const extractTeamIdsFromEntityAssociations = (nodes: (ORG_ROLES | TEAM_ROLES)[] | undefined): { teamIds: string[] | undefined } => {
    if (!nodes) {
        return {
            teamIds: undefined,
        };
    }
    const learnerAssociation: TEAM_ROLES[] = nodes.filter(
        (entityAssociation): entityAssociation is TEAM_ROLES =>
            entityAssociation.parentResource?.__typename === AssociationResourceType.TEAM &&
            entityAssociation.userRoles?.some(({ name: roleName }) => roleName === RoleV2.LEARNER),
    );
    return {
        teamIds: learnerAssociation.map((association) => association.parentResource.id),
    };
};

type UserRequestNode = {
    id: string;
    userRequestStatus: string;
    userRequestResourceType: string;
    resourceIds: string[];
    userRequestExpiredAt: string;
    organization: {
        id: string;
    };
};

export type UserRequestData = {
    resourceIds: string[];
    organization: { id: string };
    userRequestStatus: string;
};

export const extractUserRequestsFromCurrentUser = (nodes: UserRequestNode[] | undefined): UserRequestData[] | undefined => {
    if (!nodes || nodes.length === 0) {
        return undefined;
    }
    return nodes.map((node) => ({
        resourceIds: node.resourceIds,
        organization: {
            id: node.organization.id,
        },
        userRequestStatus: node.userRequestStatus,
    }));
};
